import SText from "@/styles/SText";
import { InspectionOrder, InspectionOrderAdd } from "@/types/inspection";
import { App, Button, DatePicker, Flex, Form, Input, Modal, notification, Spin, TableProps, Tag, Tooltip } from "antd";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import STableLayout from "@/styles/STableLayout";
import { AnyObject } from "antd/es/_util/type";

import { Product, ProductStoreRequire } from "@/types/product";
import { orderAdd } from "@/apis/inspection";
import { productFetchs, productReqDel } from "@/apis/product";
import dayjs, { Dayjs } from "dayjs";
import InventorySelectMulti from "@/components/Selector/InventorySelectMulti";
import { InventoryV2 } from "@/types/inventoryV2";
import ProductInspectConfig from "./ProductInspectConfig";
import useSourceType from "@/hooks/useSourceType";
import ProductStoreReqModal from "@/pages/Product/components/ProductStoreReqModal";

interface AddInspectionProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (order: InspectionOrder) => void;
};

const AddInspection: FC<AddInspectionProps> = ({ open, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const { getProductStore } = useSourceType();
  const { modal } = App.useApp();

  const [products, setProducts] = useState<Product[]>([]);
  const [planAt, setPlanAt] = useState<Dayjs>(dayjs());
  const [desc, setDesc] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [openInventory, setOpenInventory] = useState<boolean>(false);
  const [errId, setErrId] = useState<string>('');
  const [selectProIds, setSelectProIds] = useState<Array<string>>([]);
  const [configProduct, setConfigProduct] = useState<Product | null>(null);
  const [reqModal, setReqModal] = useState<{ product: Product; detail: ProductStoreRequire | null } | null>(null);


  // 从库存选择：取库存对应的物料（按物料 id 去重），新增的物料通过接口获取完整信息
  const handleInventory = async (data: InventoryV2[]) => {
    const existIds = new Set(products.map(item => item.id));
    const newIds = Array.from(new Set(
      data.map(item => item.product?.id).filter((id): id is string => !!id && !existIds.has(id))
    ));
    if (newIds.length === 0) return;

    try {
      setLoading(true);
      const res = await productFetchs({ strs: newIds });
      setProducts(last => [...last, ...(res.body ?? [])]);
    } catch (error) {
      notification.error({ message: t('inspection.saveErr') });
    } finally { setLoading(false) };
  }

  const handleOk = async () => {
    if (products.length === 0) return notification.error({ message: t('inspection.plProduct') });
    if (!!!planAt.valueOf()) return notification.error({ message: t('inspection.plPlanAt') });
    setErrId('');
    try {
      setLoading(true);
      let order = await orderAdd({
        productIds: products.map(pro => pro.id),
        planAt: planAt.endOf("day").valueOf(),
        remark: desc
      });
      notification.success({ message: t('inspection.saveOk') });
      onSuccess(order);
      onClose();
    } catch (error: any) {
      let descStr: string | undefined = undefined;
      if (error?.code === 74308) {
        descStr = t('inspection.redPro')
        setErrId(error?.extra ?? '');
      }
      notification.error({ message: t('inspection.saveErr'), description: descStr });
    } finally { setLoading(false) };
  };

  const handleDel = () => {
    setProducts(last => last.filter(p => !selectProIds.includes(p.id)));
    setSelectProIds([]);
  };

  const delProStoreType = (productId: string, typeId: string) => {
    modal.confirm({
      title: t('common.tip'),
      content: t('common.rmContent'),
      onOk: async () => {
        try {
          await productReqDel(productId, typeId);
          setProducts(last => last.map(p => ({
            ...p,
            storageRequirements: (p.storageRequirements || []).filter(ps => ps.id !== typeId)
          })))
          notification.success({ message: t('common.rmSuccess') })
        } catch (error) {
          notification.error({ message: t('common.rmError') });
        }
      }
    })
  };

  const handleReqSuccess = (data: ProductStoreRequire) => {
    if (!reqModal) return;
    const productId = reqModal.product.id;
    setProducts(last => last.map(p => {
      if (p.id !== productId) return p;
      const exists = (p.storageRequirements || []).some(sr => sr.id === data.id);
      return {
        ...p,
        storageRequirements: exists
          ? (p.storageRequirements || []).map(sr => sr.id === data.id ? data : sr)
          : [...(p.storageRequirements || []), data],
      };
    }));
  };

  const columns: TableProps<Product>['columns'] = [
    {
      title: <SText text={t('product.code')} />,
      key: 'productCode',
      dataIndex: ["product", "code"],
      width: 120,
      sorter: (a, b) => {
        const valA = a?.code ?? "";
        const valB = b?.code ?? "";
        return valA.localeCompare(valB);
      },
      render: (_, record) => <SText text={record?.code || ''} style={{ width: "100%", color: errId === record.id ? "red" : "unset" }} />
    },
    {
      title: <SText text={t('product.name')} />,
      key: 'productName',
      width: 150,
      render: (_, record) => <SText text={record?.name || ''} style={{ width: "100%" }} />
    },
    {
      title: <SText text={t('order.brand')} />,
      key: 'productModel',
      width: 120,
      render: (_, record) => <SText text={record?.model || ''} style={{ width: "100%" }} />
    },
    {
      title: <SText text={t('product.uom')} />,
      key: 'uom',
      width: 80,
      align: 'center',
      render: (_, record) => <SText text={record?.uom || ''} style={{ width: "100%" }} />
    },
    {
      title: <SText text={t('product.storeType')} />,
      key: 'storeType',
      width: 200,
      align: 'center',
      render: (_, record) => (
        <Flex gap={2} wrap>
          {
            record.storageRequirements.map(sr => {
              let info = getProductStore(sr.storageCode);
              return (
                info && <Tooltip title={
                  <div>
                    <div>{t("product.minValue")}: {sr.minValue}</div>
                    <div>{t("product.maxValue")}: {sr.maxValue}</div>
                    <div>{t("product.unit")}: {sr.unit}</div>
                    <div>{t("product.inspectionCycleDays")}: {sr.inspectionCycleDays}</div>
                  </div>
                }>
                  <div style={{ cursor: "pointer" }}>
                    <Tag onClick={() => setReqModal({ product: record, detail: sr })} color={info.color} closable onClose={(e) => {
                      e.preventDefault();
                      delProStoreType(record.id, sr.id);
                    }}>{info.label}</Tag>
                  </div>
                </Tooltip>
              )
            })
          }
          <Button type="dashed" onClick={() => setReqModal({ product: record, detail: null })}>{t('common.add')}</Button>
        </Flex>
      )
    },
    {
      title: t("common.opt"),
      fixed: "right",
      width: 180,
      render: (_, record) => (
        <Button type="link" onClick={() => setConfigProduct(record)}>{t('inspection.config')}</Button>
      )
    },
  ];

  const headerChildren = (
    <Flex gap={16} style={{ width: "100%" }}>
      <Form.Item layout="horizontal" label={t('inspection.inspectedAt')} style={{ margin: 0 }} required >
        <DatePicker value={planAt} onChange={setPlanAt} />
      </Form.Item>
      <Form.Item layout="horizontal" label={t('inspection.remark')} style={{ margin: 0, flex: 1 }} >
        <Input value={desc}
          onChange={e => setDesc(e.target.value)} style={{ flex: 1 }}
          placeholder={t('inspection.remark')} />
      </Form.Item>
    </Flex>
  );

  const headerSub = (
    <Flex gap={8} style={{ marginBottom: 6 }}>
      <Button type="primary" onClick={() => setOpenInventory(true)}>{t('inspection.addProduct')}</Button>
      <Button danger onClick={handleDel}>{t('inspection.del')}</Button>
    </Flex>
  )

  return (
    <Modal title={t('inspection.addOrder')} open={open} width={1200} onCancel={onClose} onOk={handleOk}>
      <Spin spinning={loading}>
        <Flex vertical style={{ height: "70vh" }}>
          <STableLayout
            pagination={false}
            headerChildren={headerChildren}
            headerSub={headerSub}
            total={products.length}
            dataSource={products}
            extHeight={100}
            columns={columns as AnyObject[]}
            scrollX={1000}
            rowKey="id"
            rowSelection={{
              type: "checkbox",
              fixed: true,
              selectedRowKeys: selectProIds,
              onChange: (keys: React.Key[]) => {
                setSelectProIds(keys as string[]);
              },
            }}
          />
        </Flex>
        {openInventory && <InventorySelectMulti openInventory={openInventory} setOpenInventory={setOpenInventory} handleOk={handleInventory} />}
        {reqModal && <ProductStoreReqModal
          product={reqModal.product}
          detail={reqModal.detail}
          open={!!reqModal}
          onSuccess={handleReqSuccess}
          onClose={() => setReqModal(null)}
        />}
        {configProduct && <ProductInspectConfig product={configProduct} open={!!configProduct} onClose={() => setConfigProduct(null)} />}
      </Spin>
    </Modal>
  )
};

export default AddInspection;